import { useState } from 'react';
import { Search, Download } from 'lucide-react';

import type { StoredCaptureEvent } from '../../lib/types';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

import { SmartMessage, JsonHighlight } from './json-highlight';

const CAPABILITY_COLOR: Record<string, string> = {
  fetch: 'var(--flux-fetch)',
  console: 'var(--flux-console)',
  env: 'var(--flux-env)',
  vm: 'var(--flux-vm)',
  time: 'var(--flux-time)',
  crypto: 'var(--flux-crypto)',
  math: 'var(--flux-math)',
  checkpoint: 'var(--flux-checkpoint)',
  timer: 'var(--flux-timer)',
  abort: 'var(--flux-abort)',
};

/** Display name overrides — only for labels; internal capability key is unchanged */
const CAPABILITY_DISPLAY: Record<string, string> = {
  vm: 'function',
};

function displayCap(capability: string): string {
  return CAPABILITY_DISPLAY[capability] ?? capability;
}

function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 2,
    hour12: false,
  });
}

function phaseColor(phase: string): string {
  if (phase === 'error') return 'text-[var(--flux-error)]';
  if (phase === 'response') return 'text-[var(--flux-success)]/70';
  return 'text-[var(--flux-fetch)]/50';
}

interface EventsPanelProps {
  events: StoredCaptureEvent[];
  startAt: number;
  onSelect: (idx: number) => void;
}

const CAPABILITY_FILTERS = [
  'fetch',
  'console',
  'env',
  'function',
  'math',
  'crypto',
  'timer',
  'checkpoint',
  'abort',
  'time',
] as const;

const FILTER_TO_CAPABILITY: Record<string, string> = {
  function: 'vm',
};

function filterCapability(chip: string): string {
  return FILTER_TO_CAPABILITY[chip] ?? chip;
}

function isProtected(payload: unknown): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as Record<string, unknown>)['__protected'] === true
  );
}

export function EventsPanel({
  events,
  startAt,
  onSelect,
}: EventsPanelProps): import('react/jsx-runtime').JSX.Element {
  const [search, setSearch] = useState('');
  const [expandedSeq, setExpandedSeq] = useState<number | null>(null);
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  const toggleFilter = (cap: string): void => {
    setActiveFilters((prev): Set<string> => {
      const next = new Set(prev);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return next;
    });
  };

  const filtered = events.filter((e, _idx): boolean => {
    if (activeFilters.size > 0) {
      // filter chips may be display names ("function" maps to "vm")
      const hasMatch = [...activeFilters].some(
        (chip): boolean => filterCapability(chip) === e.capability,
      );
      if (!hasMatch) return false;
    }
    if (search) {
      const payloadStr = JSON.stringify(e.payload ?? '').toLowerCase();
      const capStr = `${e.capability}:${e.action}`.toLowerCase();
      if (!payloadStr.includes(search.toLowerCase()) && !capStr.includes(search.toLowerCase()))
        return false;
    }
    return true;
  });

  function exportJson(): void {
    const blob = new Blob([JSON.stringify(events, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `events_export.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-2 border-b shrink-0">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search events..."
            className="h-7 pl-7 text-xs"
            value={search}
            onChange={(e): void => setSearch(e.target.value)}
          />
        </div>
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={exportJson}>
          <Download className="h-3 w-3" />
          Export JSON
        </Button>
      </div>

      {/* Capability filter chips */}
      <div className="flex flex-wrap gap-1 px-4 py-1.5 border-b">
        {CAPABILITY_FILTERS.map((cap): import('react/jsx-runtime').JSX.Element => {
          const active = activeFilters.has(cap);
          return (
            <button
              key={cap}
              onClick={(): void => toggleFilter(cap)}
              style={
                active
                  ? {
                      backgroundColor: `${CAPABILITY_COLOR[cap] ?? 'var(--flux-accent)'}20`,
                      color: CAPABILITY_COLOR[cap] ?? 'inherit',
                      borderColor: `${CAPABILITY_COLOR[cap] ?? 'var(--flux-accent)'}40`,
                    }
                  : {}
              }
              className={`px-2 py-0.5 rounded border text-[10px] font-mono transition-colors ${
                active
                  ? 'border-transparent'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {cap}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-auto font-mono text-xs">
        <table className="w-full">
          <thead className="sticky top-0 bg-background">
            <tr className="text-muted-foreground border-b">
              <th className="px-2 py-1 text-right font-medium w-8">seq</th>
              <th className="px-2 py-1 text-left font-medium">id</th>
              <th className="px-2 py-1 text-left font-medium w-16">phase</th>
              <th className="px-2 py-1 text-right font-medium w-24">time</th>
              <th className="px-2 py-1 text-right font-medium w-16">+offset</th>
              <th className="px-2 py-1 text-right font-medium w-14">dur</th>
              <th className="px-2 py-1 text-left font-medium">payload</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((event, mapIdx): import('react/jsx-runtime').JSX.Element => {
              const originalIdx = events.indexOf(event);
              const offset = Math.round(event.at - startAt);
              const isExpanded = expandedSeq === event.seq;

              return (
                <tr
                  key={`${event.id}-${mapIdx}`}
                  onClick={(): void => {
                    setExpandedSeq(isExpanded ? null : event.seq);
                    onSelect(originalIdx);
                  }}
                  className={`cursor-pointer hover:bg-muted/50 border-b border-border/30 ${
                    event.phase === 'error' ? 'bg-[var(--flux-error)]/5' : ''
                  }`}
                >
                  <td className="px-2 py-1 text-right text-muted-foreground/50">{event.seq}</td>
                  <td className="px-2 py-1 truncate max-w-[10rem]">
                    <span
                      style={{ color: CAPABILITY_COLOR[event.capability] ?? 'inherit' }}
                      className="font-mono"
                    >
                      {displayCap(event.capability)}
                    </span>
                    <span className="text-muted-foreground/40">.{event.action}</span>
                    <span className="text-muted-foreground/30">:{event.seq}</span>
                  </td>
                  <td className={`px-2 py-1 ${phaseColor(event.phase)}`}>{event.phase}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-muted-foreground/50 text-[10px]">
                    {formatTime(event.at)}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                    +{offset}ms
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                    {event.durationMs != null ? `${event.durationMs}ms` : '–'}
                  </td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-xs">
                    {isExpanded ? (
                      <div className="max-h-40 overflow-auto">
                        <JsonHighlight value={event.payload} />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {isProtected(event.payload) && (
                          <span className="text-muted-foreground/50 italic">encrypted</span>
                        )}
                        <SmartMessage
                          text={JSON.stringify(event.payload).slice(0, 80)}
                          className="text-muted-foreground/60"
                        />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-muted-foreground">
            No events match the current filter
          </div>
        )}
      </div>
    </div>
  );
}
